import { Icon } from '@iconify/react';
import { CircularLoadingIndicator } from '@mindpal-co/mindpal-ui';
import type { FC } from 'react';

import Button from '@/components/Button';
import Text from '@/components/Text';
import type {
  GithubProfile,
  GithubProfileRepo,
} from '@/services/externalCandidates';
import { githubDateDifference } from '@/utils/github';

import GithubDataField from './GithubDataField';
import NoGitHubDataPlaceholder from './NoGitHubDataPlaceholder';
import Repository from './Repository';
import GithubDataSectionWrap from './SectionWrap';

type Props = {
  isLoading: boolean;
  data: GithubProfile | null;
  repoData?: GithubProfileRepo[];
  loadMoreRepos?: () => void;
  moreReposLoading?: boolean;
  isError?: boolean;
  error?: GithubProfile['errorType'];
  onRetry: () => void;
  dataCount: number;
  currentDataCount: number;
};
const GithubData: FC<Props> = ({
  data,
  repoData,
  isLoading,
  loadMoreRepos,
  moreReposLoading,
  isError,
  error,
  onRetry,
  currentDataCount,
  dataCount,
}) => {
  const githubData =
    data && repoData
      ? getGithubData({
          ...data,
          repositories: repoData,
          loadMoreRepos,
          moreReposLoading,
          currentDataCount,
          dataCount,
        })
      : null;

  const errorText = (() => {
    if (error === 'GITHUB_API')
      return {
        title: 'Oops! GitHub is having some trouble.',
        description:
          'We’re unable to connect to GitHub at the moment. It might be down, or there could be a temporary issue. Please try again later.',
      };
    if (error === 'WRONG_USERNAME')
      return {
        title: 'Oops! User not found.',
        description:
          'We couldn’t find that GitHub username. Double-check the username and try again.',
      };
    if (error === 'INTERNAL' || error === 'WRONG_API_AUTH_CREDENTIALS')
      return {
        title: 'Oops! Something went wrong on our end.',
        description:
          'It looks like we’re having some trouble connecting to GitHub. Please bear with us as we fix the issue and try again later.',
      };
    return {
      title: 'Oops! Something went wrong.',
      description:
        "We couldn't connect to GitHub right now. Please check your connection or try again later.",
    };
  })();

  if (isError)
    return (
      <div className="flex flex-1 items-center justify-center rounded-lg bg-neutral-200">
        <div className="flex max-w-[16rem] flex-col items-center justify-center gap-4">
          <Icon
            className="text-3xl"
            icon="material-symbols-light:sentiment-sad-outline"
          />
          <div>
            <Text className="text-center font-semibold">{errorText.title}</Text>
            <Text
              className="text-center text-neutral-700"
              variant="body-caption"
            >
              {errorText.description}
            </Text>
          </div>
          <Button
            onClick={onRetry}
            className="w-full text-neutral-0"
            variant="secondary"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  if (isLoading)
    return (
      <div className="flex flex-1 items-center justify-center">
        <CircularLoadingIndicator />
      </div>
    );
  if (!githubData) return <NoGitHubDataPlaceholder />;
  return (
    <>
      {githubData.map(({ content, description, title }) => (
        <GithubDataSectionWrap
          key={title}
          title={title}
          description={description}
        >
          {content}
        </GithubDataSectionWrap>
      ))}
    </>
  );
};

const getGithubData = ({
  bio,
  company,
  email,
  followers,
  following,
  githubCreatedAt,
  hireable,
  location,
  publicGistsNumber,
  publicRepositoriesNumber,
  totalContributions,
  repositories,
  summedRepositoriesStars,
  loadMoreRepos,
  moreReposLoading,
  dataCount,
  currentDataCount,
}: GithubProfile & { repositories: GithubProfileRepo[] } & {
  loadMoreRepos?: () => void;
  moreReposLoading?: boolean;
  dataCount?: number;
  currentDataCount?: number;
}) => {
  return [
    {
      title: 'Profile Summary',
      description: bio,
      content: [
        {
          label: 'Location',
          icon: 'material-symbols-light:location-on-outline',
          value: location,
        },
        {
          label: 'Company',
          icon: 'material-symbols-light:apartment',
          value: company,
        },
        {
          label: 'E-mail',
          icon: 'material-symbols-light:mail-outline',
          value: email,
        },
        {
          label: 'Hireable',
          icon: 'material-symbols-light:business-center-outline',
          value: hireable ? 'Yes' : 'No',
        },
        {
          label: 'Member Since',
          icon: 'material-symbols-light:calendar-month-outline',
          value: githubDateDifference(githubCreatedAt, new Date()),
        },
      ].map((fieldData) => (
        <GithubDataField {...fieldData} key={fieldData.label} />
      )),
    },
    {
      title: 'Statistics',
      content: [
        {
          label: 'Repository',
          icon: 'material-symbols-light:terminal',
          value: publicRepositoriesNumber?.toString(),
        },
        {
          label: 'Gists',
          icon: 'material-symbols-light:text-snippet-outline',
          value: publicGistsNumber?.toString(),
        },
        {
          label: 'Followers',
          icon: 'material-symbols-light:groups-outline',
          value: followers?.toString(),
        },
        {
          label: 'Following',
          icon: 'material-symbols-light:person-add-outline',
          value: following?.toString(),
        },
        {
          label: 'Repository Stars',
          icon: 'material-symbols-light:kid-star-outline',
          value: summedRepositoriesStars?.toString(),
        },
      ].map((fieldData) => (
        <GithubDataField {...fieldData} key={fieldData.label} />
      )),
    },
    {
      title: 'Repositories',
      description: (
        <div className="grid grid-cols-2 gap-2 py-2">
          <GithubDataField
            icon="material-symbols-light:add-ad-outline"
            label="Total Contributions"
            value={totalContributions?.toString()}
            layout="simple"
          />
          <GithubDataField
            icon="material-symbols-light:book-ribbon-outline"
            label="All public repositories"
            value={publicRepositoriesNumber?.toString()}
            layout="simple"
          />
        </div>
      ),
      content: (
        <>
          {repositories.length <= 0 && (
            <div className="col-span-2 flex flex-col items-center justify-center gap-2 rounded-lg bg-neutral-800 p-4 text-neutral-300">
              <Icon
                icon="material-symbols-light:sentiment-sad-outline"
                className="text-xl"
              />
              <Text variant="body-caption" className="text-center">
                Oops! It looks like there are no repositories here.
              </Text>
            </div>
          )}
          {repositories.map((repo) => (
            <Repository {...repo} key={repo.name} />
          ))}
          {loadMoreRepos && (
            <div className="relative col-span-2 flex items-center justify-center">
              <Button
                isLoading={moreReposLoading}
                variant="unstyled"
                className=" underline"
                onClick={loadMoreRepos}
              >
                Load more
              </Button>
              {dataCount && currentDataCount && (
                <Text className="absolute -right-0" variant="body-small">
                  <span className="font-bold text-primary-300">
                    {currentDataCount}
                  </span>{' '}
                  out of{' '}
                  <span className="font-bold text-primary-300">
                    {dataCount}
                  </span>{' '}
                  repositories
                </Text>
              )}
            </div>
          )}
        </>
      ),
    },
  ];
};

export default GithubData;
